import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { ASSETS } from "@/lib/api";

const links = [
    { to: "/", label: "Home" },
    { to: "/book", label: "The Book" },
    { to: "/network", label: "The Network" },
    { to: "/ecosystem", label: "The Ecosystem" },
    { to: "/directory", label: "Directory" },
    { to: "/founder-stories", label: "Founder Stories" },
    { to: "/about", label: "About" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [pathname]);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-[#F6F1E7]/85 backdrop-blur-xl border-b border-[#26352E]/10"
                    : "bg-transparent"
            }`}
        >
            <div className="container-x mx-auto flex h-16 max-w-7xl items-center justify-between">
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="flex items-center gap-2.5 font-display text-lg font-bold tracking-tight"
                >
                    <img
                        src={ASSETS.logo}
                        alt="Purposeful, Inc. logo"
                        loading="eager"
                        decoding="async"
                        className="h-8 w-8 rounded-md ring-1 ring-white/10 object-cover"
                    />
                    <span>Purposeful<span className="text-[#D4AF37]">,</span> Inc.</span>
                </Link>

                <nav className="hidden items-center gap-7 lg:flex">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}-link`}
                            className={({ isActive }) =>
                                `text-sm transition-colors ${
                                    isActive
                                        ? "text-[#B3955A]"
                                        : "text-[#26352E]/85 hover:text-[#26352E]"
                                }`
                            }
                            end={l.to === "/"}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:block">
                    <Link
                        to="/join"
                        data-testid="nav-cta-waitlist"
                        className="btn-gold"
                    >
                        Join Purposeful
                    </Link>
                </div>

                <button
                    onClick={() => setOpen((o) => !o)}
                    className="lg:hidden text-[#26352E]"
                    data-testid="nav-mobile-toggle"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="nav-mobile-panel"
                    className="lg:hidden bg-[#F6F1E7]/95 border-t border-[#26352E]/10"
                >
                    <div className="container-x mx-auto max-w-7xl py-6 flex flex-col gap-4">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                data-testid={`nav-mobile-${l.label.toLowerCase()}-link`}
                                className={({ isActive }) =>
                                    `text-base ${isActive ? "text-[#B3955A]" : "text-[#26352E]"}`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/join"
                            data-testid="nav-mobile-cta-waitlist"
                            className="btn-gold mt-2 w-fit"
                        >
                            Join Purposeful
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
